import type { ColumnDef } from "@tanstack/react-table"
import clsx from "clsx"
import { match } from "ts-pattern"
import { Skeleton } from "../ui/skeleton"
import { TableBody, TableCell, TableRow } from "../ui/table"
import { useDataTableCtx } from "./DataTable"

interface InitialSkeletonProps {
  rows?: number
}

export function InitialSkeleton({ rows = 10 }: InitialSkeletonProps) {
  const { columns, initialState } = useDataTableCtx()

  const leftPinnedColumns = initialState?.columnPinning?.left ?? []

  const skeletonRows = Array.from({ length: rows }).map((_, rowIndex) => (
    <TableRow
      key={`skeleton-row-${
        // biome-ignore lint/suspicious/noArrayIndexKey: no key
        rowIndex
      }`}
      className="pointer-events-none"
    >
      {columns.map((column) => {
        const isLeftPinned = leftPinnedColumns.includes(column.id ?? "")

        return match(column)
          .with({ id: "__select__" }, () => (
            <TableCell
              className={clsx(
                "!shadow-[inset_0px_-1px_0px_0px_var(--wb-color-neutral-0)] !bg-neutral-0",
                {
                  "sticky left-0 z-[12]": isLeftPinned,
                }
              )}
              style={{
                minWidth: column?.size,
              }}
            />
          ))
          .otherwise(() => (
            <TableCell
              className={clsx("py-6 px-2", {
                "sticky z-[11r]": isLeftPinned,
              })}
              style={{
                minWidth: column.size ?? 150, // 150 is the default size of the column
                left: isLeftPinned ? getPreviousColumnSize(columns, column) : undefined,
              }}
            >
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))
      })}
    </TableRow>
  ))

  return <TableBody>{skeletonRows}</TableBody>
}

// method to get the sum of size of all previous columns
function getPreviousColumnSize(columns: ColumnDef<any, any>[], column: ColumnDef<any, any>) {
  return columns
    .slice(0, columns.indexOf(column))
    .reduce((acc: number, curr) => acc + (curr.size ?? 0), 0)
}
